import '@mui/styles';
import { createTheme } from '@mui/material/styles';

/* eslint-disable @typescript-eslint/no-empty-interface */
const fonts = [
    "Graphik",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
];
const fontsString = fonts.join(",");
const getDesignTokens = (mode) => ({
    mode,
    breakpoints: {
        values: {
            xs: 0,
            sm: 700,
            md: 900,
            lg: 1200,
            xl: 1500,
        },
    },
    typography: {
        fontSize: 12,
        fontWeightRegular: 400,
        fontWeightBold: 700,
        fontFamily: fontsString,
        h1: {
            fontSize: 70,
            lineHeight: "86px",
            fontWeight: 600,
            letterSpacing: -3,
        },
        h1Numeric: {
            fontSize: 56,
            lineHeight: "56px",
            fontWeight: 400,
            fontFamily: fontsString,
        },
        h2: {
            fontSize: 56,
            lineHeight: "68px",
            fontWeight: 600,
            letterSpacing: -2,
        },
        h3: {
            fontSize: 32,
            lineHeight: "40px",
            fontWeight: 600,
            letterSpacing: -1,
        },
        h4: {
            fontSize: 24,
            lineHeight: "36px",
            fontWeight: 600,
        },
        h5: {
            fontSize: 20,
            lineHeight: "28px",
            fontWeight: 500,
        },
        h6: {
            fontSize: 16,
            lineHeight: "24px",
            fontWeight: 500,
        },
        h7: {
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 500,
            fontFamily: fontsString,
        },
        body1: {
            fontSize: 16,
            lineHeight: "24px",
            fontWeight: 400,
        },
        body2: {
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            fontFamily: fontsString,
        },
        body3: {
            fontSize: 12,
            lineHeight: "16px",
            fontWeight: 400,
            fontFamily: fontsString,
        },
        button: {
            fontSize: 14,
            lineHeight: "20px",
            letterSpacing: 1,
            fontWeight: 500,
            textTransform: "none",
            fontFamily: fontsString,
        },
        buttonMedium: {
            fontSize: 14,
            lineHeight: "20px",
            letterSpacing: 1,
            fontWeight: 500,
            textTransform: "none",
            fontFamily: fontsString,
        },
        buttonLarge: {
            fontSize: 16,
            lineHeight: "24px",
            letterSpacing: 1,
            fontWeight: 500,
            textTransform: "none",
            fontFamily: fontsString,
        },
        buttonSmall: {
            fontSize: 12,
            lineHeight: "16px",
            letterSpacing: 1,
            fontWeight: 500,
            textTransform: "none",
            fontFamily: fontsString,
        },
        tinyBold: {
            fontSize: 12,
            lineHeight: "16px",
            fontWeight: 500,
            fontFamily: fontsString,
        },
        tiny: {
            fontSize: 12,
            lineHeight: "16px",
            fontWeight: 400,
            fontFamily: fontsString,
        },
        small: {
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            textTransform: "none",
            fontFamily: fontsString,
        },
        normal: {
            fontSize: 16,
            lineHeight: "24px",
            fontWeight: 400,
            textTransform: "none",
            fontFamily: fontsString,
        },
        caption: undefined,
        overline: undefined,
    },
    palette: {
        ui10: "#fcfcff",
        ui12: "#ffffff",
        ui15: "#ffffff",
        ui20: "#ffffff",
        ui25: "#ffffff",
        ui30: "#f3faff",
        ui40: "#fcfcfd",
        ui50: "#e0e5ea",
        ui55: "#f5f5f8",
        ui60: "#cbd0d7",
        dui10: "#131735",
        dui15: "#232952",
        dui50: "#3d427a",
        text: {
            primary: "#3d477b",
            secondary: "#696f8c",
            high: "#192353",
            medium: "#3d477b",
            low: "#696f8c",
            highlight: "#1b63cf",
            white: "#ffffff",
            dhigh: "#e1eaf3",
            dmedium: "#becbda",
            dlow: "#6773a2",
            greyLow: "#797c80",
            greyMedium: "#4d5155",
        },
        primary: {
            main: "#1b63cf",
            hover: "#1887f5",
            800: "#1b63cf",
            600: "#1887f5",
            400: "#3aa5ff",
            100: "#d2e2f9",
            50: "#e8f0fc",
        },
        progress: {
            main: "#ffa800",
        },
        success: {
            main: "#26a8a5",
            hover: "#4ab8b5",
            500: "#26a8a5",
            300: "#4ab8b5",
        },
        failure: {
            main: "#db1c1c",
            hover: "#f74a41",
            700: "#db1c1c",
            300: "#ed6d68",
        },
        background: {
            default: "#fcfcff",
            paper: "#ffffff", // ui20
        },
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: "h1",
                    h2: "h2",
                    h3: "h3",
                    h4: "h4",
                    h5: "h5",
                    h6: "h6",
                    h7: "h6",
                    h1Numeric: "h1",
                    body1: "p",
                    body2: "span",
                    body3: "span",
                    tiny: "p",
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: `
        *::-webkit-scrollbar {
          width: 4px;
        }

        *::-webkit-scrollbar-thumb {
          border-radius: 100;
          background-color: #d2d3d5;
        }
      `,
        },
    },
});
function getThemedComponents(theme) {
    return {
        components: {
            MuiButtonBase: {
                defaultProps: {
                    disableTouchRipple: true,
                },
            },
            MuiCheckbox: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        padding: 0,
                        borderRadius: 5,
                        color: theme.palette.ui50,
                        overflow: "hidden",
                        "& .MuiSvgIcon-fontSizeMedium": {
                            width: 20,
                            height: 20,
                            fontSize: 20,
                        },
                        "& .MuiSvgIcon-fontSizeSmall": {
                            width: 16,
                            height: 16,
                            fontSize: 16,
                        },
                        "&.Mui-checked": {
                            color: theme.palette.primary.main,
                        },
                        "&.Mui-disabled": {
                            opacity: "30%",
                            "&.MuiCheckbox-indeterminate": {
                                color: theme.palette.primary.main,
                            },
                        },
                    },
                },
            },
            MuiRadio: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        color: theme.palette.ui50,
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                        "&.Mui-checked": {
                            color: theme.palette.primary.main,
                        },
                        "&.Mui-disabled": {
                            color: theme.palette.ui50,
                            opacity: "30%",
                            "&.Mui-checked": {
                                color: theme.palette.primary.main,
                            },
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    elevation1: {
                        borderRadius: 4,
                        backgroundColor: theme.palette.ui20,
                        boxShadow: `
              0px 1px 1px rgba(112, 144, 176, 0.12),
              0px 4px 8px rgba(112, 144, 176, 0.05)
            `,
                    },
                    outlined: {
                        borderRadius: 4,
                        backgroundColor: theme.palette.ui20,
                        border: `1px solid ${theme.palette.ui50}`,
                    },
                },
            },
            MuiSvgIcon: {
                variants: [
                    {
                        props: { fontSize: "small" },
                        style: {
                            fontSize: "16px",
                        },
                    },
                    {
                        props: { fontSize: "medium" },
                        style: {
                            fontSize: "24px",
                        },
                    },
                    {
                        props: { fontSize: "large" },
                        style: {
                            fontSize: "32px",
                        },
                    },
                ],
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        background: "rgba(17, 31, 45, 0.3)",
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        padding: theme.spacing(0, 0, 4, 0),
                        boxSizing: "border-box",
                        "& *": {
                            boxSizing: "border-box",
                        },
                    },
                    paper: {
                        backgroundColor: theme.palette.ui20,
                        borderRadius: 8,
                        boxShadow: "none",
                        padding: 0,
                    },
                    paperWidthXs: {
                        maxWidth: 480,
                    },
                    paperWidthSm: {
                        maxWidth: 514,
                    },
                    paperWidthMd: {
                        maxWidth: 640,
                    },
                    paperWidthLg: {
                        maxWidth: 866,
                    },
                    paperWidthXl: {
                        maxWidth: 964,
                    },
                },
                defaultProps: {
                    maxWidth: "sm",
                },
            },
            MuiDialogPaper: {
                styleOverrides: {
                    root: {
                        padding: theme.spacing(0, 0, 4, 0),
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: Object.assign(Object.assign({}, theme.typography.body1), { padding: theme.spacing(3, 6, 6), margin: 0, gap: 24, justifyContent: "center", backgroundColor: theme.palette.background.paper }),
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        padding: theme.spacing(0, 6, 3),
                        marginTop: theme.spacing(3),
                        textAlign: "center",
                        [".MuiDialogTitle-root + &"]: {
                            marginTop: 0,
                        },
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        padding: theme.spacing(3.5, 2, 3, 2),
                        width: "100%",
                        borderRadius: 4,
                        display: "grid",
                        gridTemplateColumns: "50px 1fr 50px",
                        color: theme.palette.text.high,
                        boxSizing: "border-box",
                    },
                },
            },
            MuiPopper: {
                styleOverrides: {
                    root: {
                        '&.MuiTooltip-popper[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                            marginBottom: theme.spacing(1),
                        },
                        '&.MuiTooltip-popper[data-popper-placement*="right"] .MuiTooltip-tooltip': {
                            marginLeft: theme.spacing(1),
                        },
                        '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                            marginTop: theme.spacing(1),
                        },
                        '&.MuiTooltip-popper[data-popper-placement*="left"] .MuiTooltip-tooltip': {
                            marginRight: theme.spacing(1),
                        },
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: Object.assign(Object.assign({ padding: "8px 12px", backgroundColor: theme.palette.dui10, border: `1px solid ${theme.palette.dui50}` }, theme.typography.tinyBold), { boxShadow: `0px 1px 1px rgba(112, 144, 176, 0.12),
            0px 4px 8px rgba(112, 144, 176, 0.05)` }),
                    arrow: {
                        "&::before": {
                            border: `1px solid ${theme.palette.dui50}`,
                            backgroundColor: theme.palette.dui10,
                        },
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        textDecoration: "none",
                        cursor: "pointer",
                        color: theme.palette.primary.main,
                        "&:hover": {
                            textDecoration: "underline",
                        },
                        "&:visited, &:active": {
                            color: theme.palette.primary.main,
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        minHeight: 34,
                    },
                },
            },
            MuiCalendarPicker: {
                styleOverrides: {
                    root: {
                        fontFamily: theme.typography.fontFamily,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: Object.assign(Object.assign({ padding: 0, minWidth: "unset", maxWidth: "unset", minHeight: 34 }, theme.typography.h7), { letterSpacing: "unset", color: theme.palette.text.medium, "&:not(:last-child)": {
                            marginRight: theme.spacing(3),
                        }, "& > .MuiTab-iconWrapper": {
                            fontSize: 20,
                            marginRight: theme.spacing(0.5),
                        } }),
                },
            },
        },
    };
}
const lightTheme = createTheme(getDesignTokens("light"));
const theme = createTheme(lightTheme, getThemedComponents(lightTheme));

export { theme as default };
//# sourceMappingURL=index.js.map
